import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaCalendarAlt, FaUser, FaEdit, FaArrowLeft, FaTag, FaShare, FaFacebook, FaTwitter, FaWhatsapp } from 'react-icons/fa';
import BlogCard from '../components/BlogCard';

const BlogDetailPage = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [blog, setBlog] = useState(null);
    const [relatedBlogs, setRelatedBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [indexItems, setIndexItems] = useState([]);
    const contentRef = useRef(null);

    // Blog verisini çek
    useEffect(() => {
        const fetchBlogDetail = async () => {
            try {
                setLoading(true);
                const response = await fetch(`/api/blogs/${slug}`);
                const data = await response.json();

                if (data.success) {
                    setBlog(data.data.blog);          
                    setRelatedBlogs(data.data.relatedBlogs || []);
                } else {
                    setError(data.message || 'Blog yazısı bulunamadı');
                }
            } catch (err) {
                console.error('Blog detail fetch error:', err);
                setError('Blog yüklenirken hata oluştu');
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchBlogDetail();
        }
    }, [slug]);

    // Index generator
    useEffect(() => {
        if (blog && blog.content) {
            generateIndex();
        }
    }, [blog]);

    const generateIndex = () => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = blog.content;

        const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6');
        const indexItems = [];

        headings.forEach((heading, index) => {
            const id = `heading-${index}`;
            const text = heading.textContent.trim();
            const level = parseInt(heading.tagName.charAt(1));

            if (text) {
                indexItems.push({
                    id,
                    text,
                    level
                });
            }
        });

        setIndexItems(indexItems);

        // Add IDs to actual content headings
        setTimeout(() => {
            const contentElement = contentRef.current;
            if (contentElement) {
                const actualHeadings = contentElement.querySelectorAll('h1, h2, h3, h4, h5, h6');
                actualHeadings.forEach((heading, index) => {
                    heading.id = `heading-${index}`;
                    heading.style.scrollMarginTop = '100px'; // For sticky header
                });
            }
        }, 100);
    };

    const scrollToHeading = (headingId) => {
        const element = document.getElementById(headingId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    // Tarih formatla
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('tr-TR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Paylaşım URL'leri
    const shareUrls = {
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
        twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(blog?.title || '')}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${blog?.title || ''} ${window.location.href}`)}`
    };

    // İçeriği HTML olarak render et
    const renderContent = (content) => {
        if (!content) return null;

        return (
            <div
                className="blog-content"
                dangerouslySetInnerHTML={{ __html: content }}
            />
        );
    };

    // Loading state
    if (loading) {
        return (
            <Container className="py-5">
                <div className="text-center">
                    <Spinner animation="border" variant="dark" />
                    <p className="mt-2">Blog yükleniyor...</p>
                </div>
            </Container>
        );
    }

    // Error state
    if (error) {
        return (
            <Container className="py-5">
                <Alert variant="danger" className="text-center">
                    <h4>Hata!</h4>
                    <p>{error}</p>
                    <Button variant="dark" onClick={() => navigate('/blog')}>
                        Blog Sayfasına Dön
                    </Button>
                </Alert>
            </Container>
        );
    }

    // Blog bulunamadı
    if (!blog) {
        return (
            <Container className="py-5">
                <Alert variant="warning" className="text-center">
                    <h4>Blog yazısı bulunamadı</h4>
                    <Button variant="dark" onClick={() => navigate('/blog')}>
                        Blog Sayfasına Dön
                    </Button>
                </Alert>
            </Container>
        );
    }

    // Düzenleme yetkisi kontrolü
    const canEdit = currentUser && (
        currentUser.userType === 'admin' ||
        (currentUser.userType === 'representative' && blog.author._id === currentUser.id)
    );

    return (
        <div className="blog-detail-page">
            {/* Navigation */}
            <Container className="py-3">
                <Button
                    variant="link"
                    className="p-0 text-decoration-none"
                    onClick={() => navigate('/blog')}
                    style={{ color: 'var(--text-secondary)' }}
                >
                    <FaArrowLeft className="me-2" />
                    Blog Sayfasına Dön
                </Button>
            </Container>

            {/* Main Content */}
            <Container className="pb-5">
                <Row>
                    <Col lg={10} md={12}>
                        {/* Article Header */}
                        <article className="blog-detail-article">
                            {/* Badges */}
                            <div className="blog-badges mb-3">
                                <span className="badge bg-dark text-white me-2">
                                    {blog.categoryName || blog.category}
                                </span>
                                {blog.isFeatured && (
                                    <span className="badge bg-secondary text-white me-2">
                                        Öne Çıkan
                                    </span>
                                )}
                                {blog.isSticky && (
                                    <span className="badge bg-secondary text-white">
                                        Sabit
                                    </span>
                                )}
                            </div>

                            {/* Title */}
                            <h1 className="blog-detail-title">{blog.title}</h1>

                            {/* Meta Information */}
                            <div className="blog-meta d-flex align-items-center justify-content-between flex-wrap mb-4">
                                <div className="meta-left d-flex align-items-center flex-wrap">
                                    <div className="meta-item me-4">
                                        <FaCalendarAlt className="me-2" style={{ color: 'var(--text-muted)' }} />
                                        <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
                                    </div>
                                    {blog.author && (
                                        <div className="meta-item">
                                            <FaUser className="me-2" style={{ color: 'var(--text-muted)' }} />
                                            <span>
                                                {blog.author?.name || 'Yazar Bilinmiyor'}
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="meta-right">
                                    {canEdit && (
                                        <Button
                                            variant="outline-dark"
                                            size="sm"
                                            href={`/admin/edit-blog/${blog._id}`}
                                        >
                                            <FaEdit className="me-2" />
                                            Düzenle
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Excerpt */}
                            {blog.excerpt && (
                                <div className="blog-excerpt">
                                    <p className="lead text-muted">{blog.excerpt}</p>
                                </div>
                            )}

                            {/* Featured Image TEST - KIRMIZI YAZI ÇIKACAK */}
                            <div className="blog-featured-image mb-4">
                              
                                {blog.featuredImage?.url && (
                                    <img
                                        src={blog.featuredImage.url}
                                        alt={blog.featuredImage.alt || blog.title}
                                        className="img-fluid rounded shadow-sm"
                                        style={{ width: '100%', height: 'auto' }}
                                    />
                                )}
                            </div>

                            {/* Content */}
                            <div className="blog-content" ref={contentRef}>
                                {renderContent(blog.content)}
                            </div>

                            {/* Additional Images */}
                            {blog.images && blog.images.length > 0 && (
                                <div className="blog-additional-images mt-4">
                                    <Row className="g-3">
                                        {blog.images.map((image, index) => (
                                            <Col md={6} key={index}>
                                                <img
                                                    src={image.url}
                                                    alt={image.alt || `${blog.title} - Resim ${index + 1}`}
                                                    className="img-fluid rounded shadow-sm"
                                                />
                                                {image.caption && (
                                                    <small
                                                        className="d-block mt-1"
                                                        style={{ color: 'var(--text-muted)' }}
                                                    >
                                                        {image.caption}
                                                    </small>
                                                )}
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            )}

                            {/* Tags */}
                            {blog.tags && blog.tags.length > 0 && (
                                <div className="blog-tags mt-4">
                                    <div className="d-flex align-items-center flex-wrap">
                                        <FaTag className="me-2" style={{ color: 'var(--text-muted)' }} />
                                        {blog.tags.map((tag, index) => (
                                            <Badge
                                                key={index}
                                                className="me-2 mb-2"
                                                style={{
                                                    backgroundColor: 'var(--gray-200)',
                                                    color: 'var(--black)',
                                                    border: '1px solid var(--gray-300)'
                                                }}
                                            >
                                                #{tag}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Share Buttons */}
                            <div className="blog-share mt-4 pt-4 border-top">
                                <h6 className="mb-3">
                                    <FaShare className="me-2" />
                                    Bu yazıyı paylaş
                                </h6>
                                <div className="d-flex align-items-center gap-2">
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        href={shareUrls.instagram}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            borderColor: '#1877f2',
                                            color: '#1877f2'
                                        }}
                                    >
                                        <FaFacebook className="me-2" />
                                        Instagram
                                    </Button>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        href={shareUrls.whatsapp}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            borderColor: '#25d366',
                                            color: '#25d366'
                                        }}
                                    >
                                        <FaWhatsapp className="me-2" />
                                        WhatsApp
                                    </Button>
                                </div>
                            </div>
                        </article>
                    </Col>
                    <Col lg={2} className="d-none d-lg-block ps-lg-4"> {/* Added ps-lg-4 for padding */}
                        <div className="blog-sidebar sticky-top">
                            <div className="blog-index-sidebar">
                                <h6>İçindekiler</h6>
                                <div id="blog-index-content">
                                    {indexItems.length > 0 ? (
                                        <ul className="blog-index-list">
                                            {indexItems.map((item, index) => (
                                                <li
                                                    key={index}
                                                    className={`blog-index-item level-${item.level}`}
                                                    onClick={() => scrollToHeading(item.id)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    {item.text}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p style={{ color: 'var(--text-muted)' }}>
                                            Bu yazıda başlık bulunamadı
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Related Posts */}
            {relatedBlogs.length > 0 && (
                <section className="related-blogs-section py-5" style={{ backgroundColor: 'var(--gray-50)' }}>
                    <Container>
                        <h3 className="text-center mb-4">İlgili Yazılar</h3>
                        <Row>
                            {relatedBlogs.map((relatedBlog) => (
                                <Col lg={4} md={6} className="mb-4" key={relatedBlog._id}>
                                    <BlogCard blog={relatedBlog} variant="default" />
                                </Col>
                            ))}
                        </Row>
                        <div className="text-center mt-4">
                            <Link to="/blog" className="btn btn-dark">
                                Tüm Blog Yazılarını Görüntüle
                            </Link>
                        </div>
                    </Container>
                </section>
            )}

            {/* CTA SECTION - Blog Style */}
            <section className="py-5">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8}>
                  
                            <p className="lead mb-4">
                                Hayalinizdeki araca Otoprime ile ulaşın. Esnek ödeme seçenekleri ile araç sahibi olun.
                            </p>
                         
                            <Link
                                to="/application"
                                className="btn btn-outline-dark btn-lg me-3"
                            >
                                Başvuru Yap
                            </Link>
                            <Link
                                to="/contact"
                                className="btn btn-outline-dark btn-lg"
                            >
                                İletişime Geç
                            </Link>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default BlogDetailPage;